
import { Component, Prop, Ref, Vue } from 'vue-property-decorator'
import ButtonBase from '@/components/atoms/ButtonBase.vue'
import PanelFrame from '@/components/molecules/PanelFrame.vue'
import ButtonDownload from '@/components/molecules/ButtonDownload.vue'
import RoundSelect from '@/components/molecules/RoundSelect.vue'
import CompareLearningProgressTable from '@/components/organisms/manage/CompareLearningProgressTable.vue'
import { LearningProgressList } from '@/models/manage/LearningProgressList'
import { SelectItem } from '@/models/SelectItem'

/**
 * 通常学習進捗比較パネル
 */
@Component({
  components: {
    ButtonBase,
    PanelFrame,
    ButtonDownload,
    RoundSelect,
    CompareLearningProgressTable,
  },
})
export default class CompareLearningProgressPanel extends Vue {
  /** 学年フィルター */
  @Ref()
  gradeFilter!: RoundSelect

  /** 教科フィルター */
  @Ref()
  subjectFilter!: RoundSelect

  /** 教材フィルター */
  @Ref()
  studyCategoryFilter!: RoundSelect

  /** コースフィルター */
  @Ref()
  courseFilter!: RoundSelect

  /** 教室フィルター */
  @Ref()
  branchFilter!: RoundSelect

  /** 通常学習進捗集計データ一覧 */
  @Prop()
  private summaryProgressList!: LearningProgressList.Table[]

  /** 学年フィルターの選択肢 */
  @Prop()
  private filterGradeList!: SelectItem[]

  /** 学年フィルターの初期値 */
  @Prop()
  private defaultGradeCode!: string

  /** 教科フィルターの選択肢 */
  @Prop()
  private filterSubjectList!: SelectItem[]

  /** 教科フィルターの初期値 */
  @Prop()
  private defaultSubjectCode!: string

  /** 教材フィルターの選択肢 */
  @Prop()
  private filterStudyCategoryList!: SelectItem[]

  /** 教材フィルターのDisabled制御 */
  @Prop({ default: false })
  private isDisabledStudyCategoryFilter!: boolean

  /** コースフィルターの選択肢 */
  @Prop()
  private filterCourseList?: SelectItem[]

  /** コースフィルターのDisabled制御 */
  @Prop({ default: false })
  private isDisabledCourseFilter!: boolean

  /** 教室フィルターの選択肢 */
  @Prop()
  private filterBranchList?: SelectItem[]

  /** 教室フィルターのDisabled制御 */
  @Prop({ default: false })
  private isDisabledBranchFilter!: boolean

  /** 教室タブの状態: trueならactive */
  @Prop({ default: false })
  private isBranchTabActive!: boolean

  /** コースタブの状態: trueならactive */
  @Prop({ default: false })
  private isCourseTabActive!: boolean

  /** クラスタブの状態: trueならactive */
  @Prop({ default: false })
  private isClassTabActive!: boolean

  /** 教室タブクリック時のイベント */
  @Prop()
  private eventClickBranchTab?: () => void

  /** コースタブクリック時のイベント */
  @Prop()
  private eventClickCourseTab?: () => void

  /** クラスタブクリック時のイベント */
  @Prop()
  private eventClickClassTab?: () => void

  /** 学年フィルター変更時のイベント */
  @Prop()
  private changeGradeFilter!: () => void

  /** 教科フィルター変更時のイベント */
  @Prop()
  private changeSubjectFilter!: () => void

  /** 教材フィルター変更時のイベント */
  @Prop()
  private changeStudyCategoryFilter!: () => void

  /** コースフィルター変更時のイベント */
  @Prop()
  private changeCourseFilter?: () => void

  /** 教室フィルター変更時のイベント */
  @Prop()
  private changeBranchFilter?: () => void

  /** ダウンロードボタン押下時のイベント */
  @Prop()
  private eventClickDownload!: () => void

  /**
   * 選択中の学年
   *
   * @returns 学年コード
   */
  public currentGrade(): string {
    return this.gradeFilter.selectedValue()
  }

  /**
   * 選択中の教科
   *
   * @returns 教科コード
   */
  public currentSubject(): string {
    return this.subjectFilter.selectedValue()
  }

  /**
   * 選択中の教材
   *
   * @returns 教材コード
   */
  public currentStudyCategory(): string {
    return this.studyCategoryFilter.selectedValue()
  }

  /**
   * 教材フィルターの選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  public setCurrentStudyCategory(value: string): void {
    this.studyCategoryFilter.setCurrentValue(value)
  }

  /**
   * 選択中のコース
   *
   * @returns コースコード
   */
  public currentCourse(): string {
    return this.courseFilter.selectedValue()
  }

  /**
   * コースフィルターの選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  public setCurrentCourse(value: string): void {
    this.courseFilter.setCurrentValue(value)
  }

  /**
   * 選択中の教室
   *
   * @returns 教室コード
   */
  public currentBranch(): string {
    return this.branchFilter.selectedValue()
  }

  /**
   * 教室フィルターの選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  public setCurrentBranch(value: string): void {
    this.branchFilter.setCurrentValue(value)
  }

  /**
   * コースフィルターの表示制御
   *
   * @returns 表示する場合true
   */
  private isShowCourseFilter(): boolean {
    // リストが存在しない場合非表示
    if (!this.filterCourseList) {
      return false
    }

    // コース比較orクラス比較の画面で表示
    if (this.isCourseTabActive || this.isClassTabActive) {
      return true
    }

    return false
  }

  /**
   * クラスフィルターの表示制御
   *
   * @returns 表示する場合true
   */
  private isShowBranchFilter(): boolean {
    // リストが存在しない場合非表示
    if (!this.filterBranchList) {
      return false
    }

    // クラス比較の画面で表示
    if (this.isClassTabActive) {
      return true
    }

    return false
  }

  /**
   * 選択中のコースの名前
   *
   * @returns コース名
   */
  private currentCourseName(): string {
    const course = this.filterCourseList?.find((course) => {
      return course.value === this.currentCourse()
    })
    // undefinedケース
    if (!course) {
      return ''
    }
    return course.label
  }

  /**
   * 教室タブクリックイベントのラッパーメソッド(undefined時のエラー回避)
   */
  private clickBranchTab(): void {
    // undefinedブロック
    if (!this.eventClickBranchTab) {
      return
    }
    this.eventClickBranchTab()
  }

  /**
   * コースタブクリックイベントのラッパーメソッド(undefined時のエラー回避)
   */
  private clickCourseTab(): void {
    // undefinedブロック
    if (!this.eventClickCourseTab) {
      return
    }
    this.eventClickCourseTab()
  }

  /**
   * クラスタブクリックイベントのラッパーメソッド(undefined時のエラー回避)
   */
  private clickClassTab(): void {
    if (!this.eventClickClassTab) {
      return
    }
    this.eventClickClassTab()
  }

  /**
   * 選択中の教材名
   *
   * @returns 教材名
   */
  private currentStudyCategoryName(): string {
    const studyCategory = this.filterStudyCategoryList.find((studyCategory) => {
      return studyCategory.value === this.currentStudyCategory()
    })
    // undefinedケース、発生しないがブロックのため記述
    if (!studyCategory) {
      return ''
    }
    return studyCategory.label
  }
}
